import type { PanelContent } from '@/components/ui/PanelContent'
import { downloadAllToolResults } from '@/components/WorkflowToolResults/WorkflowToolResult'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'

/**
 * Panel action utilities for handling download, share, copy, and export operations
 */

export interface PanelActionContext {
  content: PanelContent
  nodeExecution?: NodeExecution
  workflowConfig?: WorkflowConfig
  executionId?: string
  getNodeExecution?: (executionId: string) => NodeExecution | undefined
}

/**
 * Downloads content based on the panel content type
 */
export async function handlePanelDownload(context: PanelActionContext): Promise<void> {
  const { content } = context

  console.log('Panel download action called with content:', content)

  try {
    switch (content.type) {
      case 'workflow-tool-result':
        await downloadWorkflowToolResult(context)
        break
      case 'file':
        await downloadFile(content)
        break
      case 'image':
        await downloadImage(content)
        break
      case 'document':
        await downloadDocument(content)
        break
      case 'spreadsheet':
        await downloadSpreadsheet(content)
        break
      default:
        toast.error('Download not supported for this content type')
    }
  } catch (error) {
    console.error('Download failed:', error)
    toast.error('Failed to download content')
  }
}

/**
 * Shares content by copying a shareable link to clipboard
 */
export async function handlePanelShare(context: PanelActionContext): Promise<void> {
  const { content, executionId } = context

  try {
    let shareUrl: string

    switch (content.type) {
      case 'workflow-tool-result':
        if (executionId) {
          shareUrl = `${process.env.NEXT_PUBLIC_APP_URI}/results/${executionId}`
        } else {
          toast.error('No execution ID available for sharing')
          return
        }
        break
      case 'file':
        if (content.filePath) {
          shareUrl = content.filePath
        } else {
          toast.error('No shareable URL available for this file')
          return
        }
        break
      case 'image':
        if (content.imageUrl) {
          shareUrl = content.imageUrl
        } else {
          toast.error('No shareable URL available for this image')
          return
        }
        break
      case 'document':
        if (content.documentUrl) {
          shareUrl = content.documentUrl
        } else {
          toast.error('No shareable URL available for this document')
          return
        }
        break
      case 'spreadsheet':
        if (content.spreadsheetUrl) {
          shareUrl = content.spreadsheetUrl
        } else {
          toast.error('No shareable URL available for this spreadsheet')
          return
        }
        break
      default:
        toast.error('Share not supported for this content type')
        return
    }

    await navigator.clipboard.writeText(shareUrl)
    toast.success('Share link copied to clipboard')
  } catch (error) {
    console.error('Share failed:', error)
    toast.error('Failed to copy share link')
  }
}

/**
 * Copies content to clipboard
 */
export async function handlePanelCopy(context: PanelActionContext): Promise<void> {
  const { content } = context

  try {
    let textToCopy: string

    switch (content.type) {
      case 'workflow-tool-result':
        textToCopy = await extractWorkflowToolResultText(context)
        break
      case 'file':
        textToCopy = await extractFileContent(content)
        break
      case 'image':
        textToCopy = content.imageUrl || content.fileName || 'Image content'
        break
      case 'document':
        textToCopy = await extractDocumentContent(content)
        break
      default:
        toast.error('Copy not supported for this content type')
        return
    }

    await navigator.clipboard.writeText(textToCopy)
    toast.success('Content copied to clipboard')
  } catch (error) {
    console.error('Copy failed:', error)
    toast.error('Failed to copy content')
  }
}

/**
 * Exports content as markdown and downloads it
 */
export async function handlePanelExportMarkdown(context: PanelActionContext): Promise<void> {
  const { content } = context

  try {
    let markdownContent: string

    switch (content.type) {
      case 'workflow-tool-result':
        markdownContent = await extractWorkflowToolResultMarkdown(context)
        break
      case 'file':
        markdownContent = await extractFileContentAsMarkdown(content)
        break
      case 'image':
        markdownContent = `![${content.fileName || 'Image'}](${content.imageUrl})`
        break
      case 'document':
        markdownContent = await extractDocumentContentAsMarkdown(content)
        break
      case 'node-execution-details':
        const nodeExecution = content.executionId
          ? context.getNodeExecution?.(content.executionId)
          : undefined
        markdownContent = await extractNodeExecutionDetailsMarkdown(content, nodeExecution)
        break
      case 'workflow-details':
        markdownContent = await extractWorkflowDetailsMarkdown(content)
        break
      default:
        toast.error('Export not supported for this content type')
        return
    }

    // Download as markdown file
    const filename = `export-${Date.now()}.md`
    const blob = new Blob([markdownContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Markdown file downloaded successfully')
  } catch (error) {
    console.error('Export failed:', error)
    toast.error('Failed to export content as markdown')
  }
}

// Helper functions for different content types

async function downloadWorkflowToolResult(context: PanelActionContext): Promise<void> {
  const { nodeExecution, workflowConfig } = context

  if (!nodeExecution || !workflowConfig) {
    toast.error('Missing workflow data for download')
    return
  }

  // Use the existing downloadAllToolResults function for single result
  await downloadAllToolResults([nodeExecution], workflowConfig)
  toast.success('Workflow result downloaded successfully')
}

async function downloadFile(content: PanelContent & { type: 'file' }): Promise<void> {
  if (!content.filePath) {
    toast.error('No download URL available')
    return
  }

  try {
    const response = await fetch(content.filePath)
    if (!response.ok) {
      throw new Error('Failed to fetch file')
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = content.fileName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('File downloaded successfully')
  } catch (error) {
    console.error('File download failed:', error)
    toast.error('Failed to download file')
  }
}

async function downloadImage(content: PanelContent & { type: 'image' }): Promise<void> {
  console.log('Downloading image with content:', content)
  const downloadUrl = content.downloadUrl || content.imageUrl
  console.log('Using download URL:', downloadUrl)

  if (!downloadUrl) {
    console.error('No image URL available. Content:', content)
    toast.error('No image URL available')
    return
  }

  try {
    const response = await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error('Failed to fetch image')
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = content.fileName || `image-${Date.now()}.${getImageExtension(downloadUrl)}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Image downloaded successfully')
  } catch (error) {
    console.error('Image download failed:', error)
    toast.error('Failed to download image')
  }
}

async function downloadDocument(content: PanelContent & { type: 'document' }): Promise<void> {
  const downloadUrl = content.downloadUrl || content.documentUrl
  if (!downloadUrl) {
    toast.error('No document URL available')
    return
  }

  try {
    const response = await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error('Failed to fetch document')
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = content.fileName || `document-${Date.now()}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Document downloaded successfully')
  } catch (error) {
    console.error('Document download failed:', error)
    toast.error('Failed to download document')
  }
}

async function downloadSpreadsheet(content: PanelContent & { type: 'spreadsheet' }): Promise<void> {
  const downloadUrl = content.downloadUrl || content.spreadsheetUrl
  if (!downloadUrl) {
    toast.error('No spreadsheet URL available')
    return
  }

  try {
    const response = await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error('Failed to fetch spreadsheet')
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = content.fileName || `spreadsheet-${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Spreadsheet downloaded successfully')
  } catch (error) {
    console.error('Spreadsheet download failed:', error)
    toast.error('Failed to download spreadsheet')
  }
}

async function extractWorkflowToolResultText(context: PanelActionContext): Promise<string> {
  const { nodeExecution } = context

  if (!nodeExecution) {
    throw new Error('No node execution data available')
  }

  // Extract text content from node execution outputs
  const outputs = nodeExecution.outputs
  if (typeof outputs === 'string') {
    return outputs
  }

  if (typeof outputs === 'object' && outputs !== null) {
    // Try to extract meaningful text content
    if ('content' in outputs && typeof outputs.content === 'string') {
      return outputs.content
    }
    if ('text' in outputs && typeof outputs.text === 'string') {
      return outputs.text
    }
    if ('markdown' in outputs && typeof outputs.markdown === 'string') {
      return outputs.markdown
    }
    // Fallback to JSON string
    return JSON.stringify(outputs, null, 2)
  }

  return String(outputs)
}

async function extractWorkflowToolResultMarkdown(context: PanelActionContext): Promise<string> {
  const { nodeExecution, workflowConfig } = context

  if (!nodeExecution) {
    throw new Error('No node execution data available')
  }

  // Use the existing rendering utilities to convert to markdown
  const { processRawOutputs, inferredContentToMarkdown } = await import(
    '@/components/WorkflowToolResults/shared/renderingUtils'
  )

  const processedOutputs = processRawOutputs(nodeExecution, workflowConfig)

  if (processedOutputs.length === 0) {
    return `# Workflow Tool Result\n\nNo content available.`
  }

  const markdownSections = processedOutputs.map((output, index) => {
    const markdown = inferredContentToMarkdown(output)
    return `## Section ${index + 1}\n\n${markdown}`
  })

  return `# Workflow Tool Result\n\n${markdownSections.join('\n\n')}`
}

async function extractFileContent(content: PanelContent & { type: 'file' }): Promise<string> {
  // For file content, we already have the content in the panel content
  if (content.content) {
    return content.content
  }

  // Fallback: try to fetch from filePath if content is not available
  if (!content.filePath) {
    throw new Error('No file content or URL available')
  }

  try {
    const response = await fetch(content.filePath)
    if (!response.ok) {
      throw new Error('Failed to fetch file content')
    }

    const text = await response.text()
    return text
  } catch (error) {
    console.error('Failed to extract file content:', error)
    return content.fileName || 'File content unavailable'
  }
}

async function extractFileContentAsMarkdown(
  content: PanelContent & { type: 'file' }
): Promise<string> {
  const textContent = await extractFileContent(content)

  // Determine if it's already markdown or needs formatting
  if (content.fileName?.endsWith('.md') || content.fileName?.endsWith('.markdown')) {
    return textContent
  }

  // Format as code block for non-markdown files
  const language = getLanguageFromFilename(content.fileName)
  return `# ${content.fileName || 'File'}\n\n\`\`\`${language}\n${textContent}\n\`\`\``
}

async function extractDocumentContent(
  content: PanelContent & { type: 'document' }
): Promise<string> {
  const documentUrl = content.documentUrl
  if (!documentUrl) {
    throw new Error('No document URL available')
  }

  try {
    const response = await fetch(documentUrl)
    if (!response.ok) {
      throw new Error('Failed to fetch document content')
    }

    const text = await response.text()
    return text
  } catch (error) {
    console.error('Failed to extract document content:', error)
    return content.fileName || 'Document content unavailable'
  }
}

async function extractDocumentContentAsMarkdown(
  content: PanelContent & { type: 'document' }
): Promise<string> {
  const textContent = await extractDocumentContent(content)

  // For documents, try to format as markdown
  return `# ${content.fileName || 'Document'}\n\n${textContent}`
}

// Utility functions

function getImageExtension(url: string): string {
  const match = url.match(/\.([^\s\n.]+)$/)
  return match ? match[1] : 'png'
}

function getLanguageFromFilename(filename?: string): string {
  if (!filename) return 'text'

  const ext = filename.split('.').pop()?.toLowerCase()

  const languageMap: Record<string, string> = {
    js: 'javascript',
    ts: 'typescript',
    jsx: 'jsx',
    tsx: 'tsx',
    py: 'python',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    cs: 'csharp',
    php: 'php',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    sql: 'sql',
    html: 'html',
    css: 'css',
    scss: 'scss',
    json: 'json',
    xml: 'xml',
    yaml: 'yaml',
    yml: 'yaml',
    md: 'markdown',
    txt: 'text',
    csv: 'csv',
  }

  return languageMap[ext || ''] || 'text'
}

async function extractNodeExecutionDetailsMarkdown(
  content: PanelContent & { type: 'node-execution-details' },
  nodeExecution?: NodeExecution
): Promise<string> {
  const { nodeData } = content
  const sections: string[] = []

  // Header
  sections.push(`# ${nodeData.name || 'Node Execution Details'}`)
  sections.push(`**Tool ID:** ${nodeData.toolId || 'N/A'}`)
  sections.push(`**Node ID:** ${nodeData.id}`)
  sections.push('')

  // Execution Details
  if (nodeExecution) {
    sections.push(`## Execution Details`)
    sections.push('')
    sections.push(`- **Status:** ${nodeExecution.status || 'Unknown'}`)
    if (nodeExecution.executionId) {
      sections.push(`- **Execution ID:** \`${nodeExecution.executionId}\``)
    }
    if (nodeExecution.startedAt) {
      sections.push(`- **Started:** ${new Date(nodeExecution.startedAt).toLocaleString()}`)
    }
    if (nodeExecution.completedAt) {
      sections.push(`- **Completed:** ${new Date(nodeExecution.completedAt).toLocaleString()}`)
    }
    if (nodeExecution.credits) {
      sections.push(`- **Credits:** ${nodeExecution.credits}`)
    }
    sections.push('')

    // Inputs
    if (nodeExecution.inputs?.functionArgs) {
      sections.push(`## Inputs`)
      sections.push('')
      sections.push('```json')
      sections.push(JSON.stringify(nodeExecution.inputs.functionArgs, null, 2))
      sections.push('```')
      sections.push('')
    }

    // Outputs
    if (nodeExecution.outputs) {
      sections.push(`## Outputs`)
      sections.push('')
      sections.push('```json')
      sections.push(JSON.stringify(nodeExecution.outputs, null, 2))
      sections.push('```')
      sections.push('')
    }
  }

  // Node Description
  if (nodeData.description) {
    sections.push(`## Description`)
    sections.push('')
    sections.push(nodeData.description)
    sections.push('')
  }

  return sections.join('\n')
}

async function extractWorkflowDetailsMarkdown(
  content: PanelContent & { type: 'workflow-details' }
): Promise<string> {
  const { workflowConfig, workflowVersion } = content
  const sections: string[] = []

  // Header
  sections.push(`# ${workflowConfig.name || 'Workflow Details'}`)
  sections.push('')

  // Version info
  if (workflowVersion) {
    sections.push(`**Version:** ${workflowVersion}`)
    sections.push('')
  }

  // Description
  if (workflowConfig.description) {
    sections.push(`## Description`)
    sections.push('')
    sections.push(workflowConfig.description)
    sections.push('')
  }

  // Parameters
  const visibleParameters = workflowConfig.parameters?.properties
    ? Object.entries(workflowConfig.parameters.properties).filter(
        ([_, param]) => param.type !== 'pipedreamAccountId'
      )
    : []

  if (visibleParameters.length > 0) {
    sections.push(`## Parameters`)
    sections.push('')
    for (const [paramName, param] of visibleParameters) {
      const isRequired =
        Array.isArray(workflowConfig.parameters.required) &&
        workflowConfig.parameters.required.includes(paramName)
      sections.push(`### ${paramName}`)
      sections.push(`- **Type:** ${param.type}`)
      sections.push(`- **Required:** ${isRequired ? 'Yes' : 'No'}`)
      if (param.description) {
        sections.push(`- **Description:** ${param.description}`)
      }
      if (param.enum && Array.isArray(param.enum) && param.enum.length > 0) {
        sections.push(`- **Options:** ${param.enum.join(', ')}`)
      }
      sections.push('')
    }
  }

  // Outputs
  if (workflowConfig.outputs && workflowConfig.outputs.length > 0) {
    sections.push(`## Outputs`)
    sections.push('')
    for (const outputVar of workflowConfig.outputs) {
      sections.push(`- \`${outputVar}\``)
    }
    sections.push('')
  }

  // Node Count
  if (workflowConfig.nodes) {
    sections.push(`## Structure`)
    sections.push('')
    sections.push(`- **Total Nodes:** ${workflowConfig.nodes.length}`)
    sections.push('')
  }

  // Required Integrations
  if (workflowConfig.requiredIntegrations && workflowConfig.requiredIntegrations.length > 0) {
    sections.push(`## Required Integrations`)
    sections.push('')
    for (const integration of workflowConfig.requiredIntegrations) {
      sections.push(`- ${integration}`)
    }
    sections.push('')
  }

  return sections.join('\n')
}
